import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import VendorlyLogo from "@/components/VendorlyLogo";
import { ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send reset link");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text flex flex-col dot-bg" data-testid="forgot-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl w-full mx-auto px-6 lg:px-10 py-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <VendorlyLogo size={30} />
          <span className="font-heading font-bold text-xl tracking-tight">Vendorly</span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md bg-white border border-brand-border rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(44,42,41,0.06)]">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <div className="w-14 h-14 mx-auto rounded-full bg-brand-success/15 flex items-center justify-center">
                <EnvelopeSimple size={28} weight="duotone" className="text-brand-success" />
              </div>
              <h1 className="font-heading text-2xl font-bold mt-4">Check your inbox</h1>
              <p className="text-brand-text/70 mt-2">
                If an account exists for <span className="font-semibold text-brand-text">{email}</span>, we've sent a reset link. It expires in 1 hour.
              </p>
              <Link to="/login" className="mt-8 inline-flex items-center gap-2 border border-brand-border rounded-full px-6 py-3 font-semibold hover:bg-brand-surface" data-testid="forgot-back">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Recover access</div>
              <h1 className="font-heading font-bold text-3xl tracking-tight">Forgot password?</h1>
              <p className="text-brand-text/70 mt-2">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <label className="text-sm font-semibold">Email</label>
                  <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                    className="mt-1.5 w-full h-12 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition-colors"
                    data-testid="forgot-email" placeholder="you@shop.com" />
                </div>
                <button type="submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold transition-colors disabled:opacity-60"
                  data-testid="forgot-submit">
                  {busy ? "Sending…" : (<>Send reset link <ArrowRight size={18} weight="bold" /></>)}
                </button>
              </form>
              <div className="mt-6 text-sm text-brand-text/70">
                Remembered it? <Link to="/login" className="text-brand-primary font-semibold hover:underline" data-testid="forgot-to-login">Sign in</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
